import React from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, LabelList } from "recharts";
import { parseMoney, parsePct, years, fmtBig, grow, filled, DECK_GREEN } from "./deck";

const txt = (t) => (typeof t === "string" ? t : t?.text || t?.name || "");

export function MarketHero({ market = {}, block = {} }) {
  const tam = market.tam || {};
  const size = +block.size || +tam.tamNum || parseMoney(tam.value || tam.tam || tam.total);
  const cagr = +block.cagr || parsePct(tam.cagr);
  const ys = years(tam.value), cy = years(tam.cagr);
  const baseYear = +block.baseYear || ys[0] || cy[0] || new Date().getFullYear();
  const lastCy = cy.length ? cy[cy.length - 1] : 0;
  const endYear = +block.endYear || (lastCy > baseYear ? lastCy : baseYear + 9);
  const span = Math.max(1, endYear - baseYear);
  const forecast = +block.forecast || grow(size, cagr, span);
  const cons = (Array.isArray(market.consolidators) ? market.consolidators : []).map((c) => c.name || c).filter(Boolean).slice(0, 3);
  const tails = (Array.isArray(market.tailwinds) ? market.tailwinds : []).map(txt).filter(Boolean);
  const drivers = filled(block.drivers, "title").length ? filled(block.drivers, "title") : [
    { title: "Aging owners", text: "Large cohort of founder-owned operators reaching retirement without family succession." },
    { title: "Fragmented supply", text: tails[0] || "Thousands of single-location operators; national share is de minimis. Off-market pipeline is deep." },
    { title: "Recurring economics", text: tails[1] || "Service and repeat revenue at higher margin — durable through cycles." },
    { title: "Active precedents", text: cons.length ? `${cons.join(", ")} actively consolidating the category.` : "PE-backed consolidators are actively buying in the category." },
  ];
  if (!size) return null;
  const unit = size >= 1e9 ? 1e9 : 1e6, uLbl = size >= 1e9 ? "$B" : "$M";
  const step = span >= 8 ? 2 : 1;
  const pts = [];
  for (let y = baseYear; y <= endYear; y += step) pts.push(y);
  if (pts[pts.length - 1] !== endYear) pts.push(endYear);
  const data = pts.map((y) => ({ year: y, v: +(grow(size, cagr, y - baseYear) / unit).toFixed(1) }));
  const headline = block.headline || `A ${fmtBig(size)} market growing ${cagr.toFixed(1)}% annually — ${market.sector ? `${market.sector.toLowerCase()} ` : ""}distribution ripe for consolidation`;
  const sources = block.sources || tam.note || "";
  return (
    <div className="ir-gap" data-testid="ir-market-hero">
      <div className="ir-deck-head">
        <span className="ir-eyebrow">Market opportunity</span>
        <h2 className="ir-deck-h md" data-testid="ir-market-headline">{headline}</h2>
      </div>
      <div className="mk-hero-grid">
        <div className="mk-panel" data-testid="ir-market-panel">
          <span className="mk-panel-k">{market.sector ? `${market.sector} market` : "Addressable market"}</span>
          <div className="mk-stat"><b data-testid="ir-market-size">{fmtBig(size)}</b><span>{baseYear} market size</span></div>
          <div className="mk-stat"><b data-testid="ir-market-forecast">{fmtBig(forecast)}</b><span>{endYear} forecast</span></div>
          <div className="mk-stat"><b data-testid="ir-market-cagr">{cagr.toFixed(1)}%</b><span>CAGR {baseYear}–{endYear}</span></div>
          {sources && <div className="mk-panel-src">Sources: {sources}</div>}
        </div>
        <div>
          <div className="mk-chart-title">{market.sector ? `${market.sector} market size — ${uLbl}` : `Market size — ${uLbl}`}</div>
          {size > 0 && (
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={data} margin={{ top: 22, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                <XAxis dataKey="year" tick={{ fontSize: 11, fill: "var(--text3)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10.5, fill: "var(--text3)" }} axisLine={false} tickLine={false} />
                <Bar dataKey="v" fill={DECK_GREEN} radius={[2, 2, 0, 0]} maxBarSize={56}><LabelList dataKey="v" position="top" formatter={(v) => `$${v}`} style={{ fontSize: 11, fill: "var(--text)" }} /></Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
          <div className="mk-drivers-box" data-testid="ir-market-drivers">
            <b className="mk-drivers-h">Why consolidation now</b>
            <div className="mk-drivers">{drivers.map((d, i) => <React.Fragment key={i}><b>{d.title}</b><span>{d.text}</span></React.Fragment>)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
